/**
 * Абстракция хранилища для Zustand persist middleware
 * Поддерживает как localStorage, так и IndexedDB
 */

/**
 * Интерфейс хранилища, совместимый с Zustand
 */
export interface StateStorage {
  getItem: (name: string) => string | null | Promise<string | null>;
  setItem: (name: string, value: string) => void | Promise<void>;
  removeItem: (name: string) => void | Promise<void>;
}

/**
 * Конфигурация для IndexedDB хранилища
 */
interface IndexedDBStorageConfig {
  /** Имя базы данных */
  dbName: string;
  /** Имя хранилища (store) */
  storeName: string;
  /** Версия базы данных */
  version?: number;
}

/**
 * Открытие соединения с IndexedDB
 * Автоматически обрабатывает миграцию при добавлении новых object stores
 */
function openDB(config: IndexedDBStorageConfig): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Сначала открываем базу без указания версии для проверки
    const checkRequest = indexedDB.open(config.dbName);

    checkRequest.onsuccess = () => {
      const db = checkRequest.result;
      const currentVersion = db.version;
      db.close();

      // Проверяем, существует ли нужный object store
      const needsUpgrade = !db.objectStoreNames.contains(config.storeName);

      if (needsUpgrade) {
        // Если нужно добавить store, открываем с новой версией
        const upgradeRequest = indexedDB.open(config.dbName, currentVersion + 1);

        upgradeRequest.onerror = () => {
          reject(new Error(`Failed to upgrade IndexedDB: ${upgradeRequest.error?.message}`));
        };

        upgradeRequest.onsuccess = () => {
          resolve(upgradeRequest.result);
        };

        upgradeRequest.onupgradeneeded = (event) => {
          const upgradeDb = (event.target as IDBOpenDBRequest).result;
          if (!upgradeDb.objectStoreNames.contains(config.storeName)) {
            upgradeDb.createObjectStore(config.storeName);
          }
        };
      } else {
        // Если store существует, открываем нормально
        const normalRequest = indexedDB.open(config.dbName, config.version ?? 1);

        normalRequest.onerror = () => {
          reject(new Error(`Failed to open IndexedDB: ${normalRequest.error?.message}`));
        };

        normalRequest.onsuccess = () => {
          resolve(normalRequest.result);
        };
      }
    };

    checkRequest.onerror = () => {
      reject(new Error(`Failed to check IndexedDB: ${checkRequest.error?.message}`));
    };

    checkRequest.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(config.storeName)) {
        db.createObjectStore(config.storeName);
      }
    };
  });
}

/**
 * Адаптер хранилища на основе IndexedDB
 *
 * Преимущества перед localStorage:
 * - Большой объём хранения (не ограничен 5-10 МБ)
 * - Асинхронный API (не блокирует главный поток)
 * - Лучше для хранения больших объектов (gitState, commits)
 */
export function createIndexedDBStorage(config: IndexedDBStorageConfig): StateStorage {
  let dbPromise: Promise<IDBDatabase> | null = null;

  const getDB = () => {
    if (!dbPromise) {
      dbPromise = openDB(config);
    }
    return dbPromise;
  };

  return {
    async getItem(name: string): Promise<string | null> {
      try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
          const transaction = db.transaction(config.storeName, 'readonly');
          const store = transaction.objectStore(config.storeName);
          const request = store.get(name);

          request.onerror = () => {
            console.error(`IndexedDB getItem error: ${request.error?.message}`);
            resolve(null);
          };

          request.onsuccess = () => {
            resolve(request.result ?? null);
          };
        });
      } catch (error) {
        console.error('IndexedDB getItem failed:', error);
        return null;
      }
    },

    async setItem(name: string, value: string): Promise<void> {
      try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
          const transaction = db.transaction(config.storeName, 'readwrite');
          const store = transaction.objectStore(config.storeName);
          const request = store.put(value, name);

          request.onerror = () => {
            console.error(`IndexedDB setItem error: ${request.error?.message}`);
            resolve();
          };

          request.onsuccess = () => {
            resolve();
          };
        });
      } catch (error) {
        console.error('IndexedDB setItem failed:', error);
      }
    },

    async removeItem(name: string): Promise<void> {
      try {
        const db = await getDB();
        return new Promise((resolve, reject) => {
          const transaction = db.transaction(config.storeName, 'readwrite');
          const store = transaction.objectStore(config.storeName);
          const request = store.delete(name);

          request.onerror = () => {
            console.error(`IndexedDB removeItem error: ${request.error?.message}`);
            resolve();
          };

          request.onsuccess = () => {
            resolve();
          };
        });
      } catch (error) {
        console.error('IndexedDB removeItem failed:', error);
      }
    },
  };
}

/**
 * Fallback хранилище на localStorage
 * Используется если IndexedDB недоступен
 */
export function createLocalStorageStorage(): StateStorage {
  return {
    getItem: (name: string) => {
      try {
        return localStorage.getItem(name);
      } catch {
        return null;
      }
    },
    setItem: (name: string, value: string) => {
      try {
        localStorage.setItem(name, value);
      } catch (error) {
        console.error('localStorage setItem failed:', error);
      }
    },
    removeItem: (name: string) => {
      try {
        localStorage.removeItem(name);
      } catch (error) {
        console.error('localStorage removeItem failed:', error);
      }
    },
  };
}

/**
 * Проверка доступности IndexedDB
 */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Создание хранилища с автоматическим выбором backend
 * Предпочитает IndexedDB, fallback на localStorage
 */
export function createStorage(config: IndexedDBStorageConfig): StateStorage {
  if (isIndexedDBAvailable()) {
    return createIndexedDBStorage(config);
  }
  console.warn('IndexedDB not available, falling back to localStorage');
  return createLocalStorageStorage();
}

/**
 * Миграция данных из localStorage в IndexedDB
 */
export async function migrateFromLocalStorage(
  localStorageKey: string,
  indexedDBStorage: StateStorage
): Promise<boolean> {
  try {
    const localData = localStorage.getItem(localStorageKey);
    if (localData) {
      await indexedDBStorage.setItem(localStorageKey, localData);
      // Удаляем из localStorage после успешной миграции
      localStorage.removeItem(localStorageKey);
      console.log(`Migrated ${localStorageKey} from localStorage to IndexedDB`);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Migration from localStorage failed:', error);
    return false;
  }
}
